'use client';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { cn } from '../../lib/utils/classnameVariants';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    portalContainer?: HTMLElement | null;
  }
>(
  (
    {
      className,
      align = 'center',
      sideOffset = 4,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      portalContainer,
      ...props
    },
    ref,
  ) => {
    // Prevent the popover from closing when interacting with nested Radix Select
    // dropdowns (their content is portalled and otherwise counted as an outside
    // interaction by the parent DismissableLayer).
    const isSelectElement = (el: HTMLElement | null) =>
      !!el &&
      (el.closest('[data-select-content]') || el.closest('[data-select-trigger]') || el.closest('[role="listbox"]'));

    const handleInteractOutside: typeof onInteractOutside = e => {
      const target = e.target as HTMLElement | null;
      if (isSelectElement(target)) {
        e.preventDefault();
        return;
      }
      onInteractOutside?.(e);
    };

    const handlePointerDownOutside: typeof onPointerDownOutside = e => {
      const target = e.target as HTMLElement | null;
      if (isSelectElement(target)) {
        e.preventDefault();
        return;
      }
      onPointerDownOutside?.(e);
    };

    const handleFocusOutside: typeof onFocusOutside = e => {
      const target = e.target as HTMLElement | null;
      if (isSelectElement(target)) {
        e.preventDefault();
        return;
      }
      onFocusOutside?.(e);
    };

    return (
      <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          onInteractOutside={handleInteractOutside}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          className={cn(
            'w-72 z-50 origin-[--radix-popover-content-transform-origin] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverContent, PopoverTrigger };
